import React, { useMemo, useState } from 'react';
import { FlatList, Modal, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Segmented } from '@/components/ui/Segmented';
import { TextField } from '@/components/ui/TextField';
import { OutBadge } from '@/components/wc/OutBadge';
import { useWcEliminatedTeams } from '@/features/wc/eliminations';
import type { PlayerWithTeam } from '@/lib/data/provider';
import { formatPrice } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { Position } from '@scoring/rules.ts';

export function PlayerPickerModal({
  visible,
  position,
  pool,
  pickedIds,
  onPick,
  onClose,
  hidePrice = false,
}: {
  visible: boolean;
  position: Position | null;
  pool: PlayerWithTeam[];
  pickedIds: Set<string>;
  onPick: (player: PlayerWithTeam) => void;
  onClose: () => void;
  /** Hide prices + the sort toggle (e.g. ToTR prediction, where cost is moot);
      players are then always ranked best-first by star power. */
  hidePrice?: boolean;
}) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const insets = useSafeAreaInsets();
  const { isEliminated } = useWcEliminatedTeams();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'price' | 'name'>('price');

  const candidates = useMemo(() => {
    const query = search.trim().toLowerCase();
    return pool
      .filter((player) => (position ? player.position === position : true))
      .filter((player) => !pickedIds.has(player.id))
      .filter((player) =>
        query.length === 0
          ? true
          : (player.known_as ?? player.name).toLowerCase().includes(query) ||
            player.name.toLowerCase().includes(query),
      )
      .sort((a, b) => {
        // Demote players from knocked-out nations to the bottom so live-nation
        // players surface first; within each group keep the price/name sort.
        const outA = isEliminated(a.team?.id) ? 1 : 0;
        const outB = isEliminated(b.team?.id) ? 1 : 0;
        if (outA !== outB) return outA - outB;
        return sort === 'price'
          ? b.fantasy_price - a.fantasy_price
          : (a.known_as ?? a.name).localeCompare(b.known_as ?? b.name);
      });
  }, [pool, position, pickedIds, search, sort, isEliminated]);

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View className="flex-1 justify-end bg-black/60">
        <View
          className="max-h-[85%] rounded-t-3xl border border-line bg-surface-2"
          style={{ paddingBottom: insets.bottom + 8 }}
        >
          <View className="items-center py-3">
            <View className="h-1 w-10 rounded-full bg-line" />
          </View>
          <View className="px-4 pb-3">
            <AppText variant="heading">
              {position
                ? t('fantasy.builder.pickForSlot', {
                    position: t(`player.positions.${position}`),
                  })
                : t('fantasy.builder.addPlayer')}
            </AppText>
          </View>
          <View className="px-4 pb-3">
            <TextField
              placeholder={t('fantasy.builder.searchPlayers')}
              value={search}
              onChangeText={setSearch}
              autoCorrect={false}
            />
          </View>
          {hidePrice ? null : (
            <View className="pb-3">
              <Segmented
                options={[
                  { value: 'price', label: t('fantasy.builder.sortPrice') },
                  { value: 'name', label: t('fantasy.builder.sortName') },
                ]}
                value={sort}
                onChange={setSort}
              />
            </View>
          )}

          {candidates.length === 0 ? (
            <EmptyState
              icon="people-outline"
              title={t('team.noPlayersTitle')}
              body={t('team.noPlayersBody')}
            />
          ) : (
            <FlatList
              data={candidates}
              keyExtractor={(item) => item.id}
              style={{ flexGrow: 0 }}
              keyboardDismissMode="on-drag"
              keyboardShouldPersistTaps="handled"
              removeClippedSubviews={false}
              renderItem={({ item }) => {
                // Soft-mark (dim + badge) players whose nation is knocked out;
                // selection stays enabled so a manager may still pick them.
                const out = isEliminated(item.team?.id);
                return (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => onPick(item)}
                    style={out ? { opacity: 0.5 } : undefined}
                    className="flex-row items-center gap-3 border-t border-line px-4 py-3 active:bg-surface/60"
                  >
                    <TeamCrest uri={item.photo_url} name={item.known_as ?? item.name} size="md" />
                    <View className="flex-1">
                      <AppText
                        variant="caption"
                        weight="semibold"
                        className="text-ink"
                        numberOfLines={1}
                      >
                        {item.known_as ?? item.name}
                      </AppText>
                      <View className="flex-row items-center gap-1.5">
                        <AppText variant="micro" numberOfLines={1} className="shrink">
                          {item.team?.name ?? ''} · {t(`player.positionsShort.${item.position}`)}
                        </AppText>
                        {out ? <OutBadge compact /> : null}
                      </View>
                    </View>
                    {hidePrice ? null : (
                      <AppText variant="body" weight="bold" tabular className="text-volt">
                        {formatPrice(item.fantasy_price, numeralOptions)}
                      </AppText>
                    )}
                  </Pressable>
                );
              }}
            />
          )}
          <View className="px-4 pt-3">
            <Button label={t('common.close')} variant="secondary" compact onPress={onClose} />
          </View>
        </View>
      </View>
    </Modal>
  );
}
